import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { storableError } from '../../util/errors';

// Image variants requested for listings embedded in CMS pages (moved here
// from the removed EditListingPage duck).
const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

// ================ Action types ================ //
const SEARCH_CUSTOM_LISTINGS_REQUEST = 'app/LandingPage/SEARCH_CUSTOM_LISTINGS_REQUEST';
const SEARCH_CUSTOM_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_CUSTOM_LISTINGS_SUCCESS';
const SEARCH_CUSTOM_LISTINGS_ERROR = 'app/LandingPage/SEARCH_CUSTOM_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  customListingIds: {},
  inProgress: false,
  error: null,
};

const resultIds = data => data.data.map(l => l.id);

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_CUSTOM_LISTINGS_REQUEST:
      return {
        ...state,
        inProgress: true,
      };

    case SEARCH_CUSTOM_LISTINGS_SUCCESS:
      return {
        ...state,
        inProgress: false,
        customListingIds: {
          ...state.customListingIds,
          [payload.id]: resultIds(payload.data),
        },
      };

    case SEARCH_CUSTOM_LISTINGS_ERROR:
      return { ...state, inProgress: false, error: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const searchCustomListingsRequest = () => ({
  type: SEARCH_CUSTOM_LISTINGS_REQUEST,
});

export const searchCustomListingsSuccess = response => ({
  type: SEARCH_CUSTOM_LISTINGS_SUCCESS,
  payload: { data: response.data, id: response.id },
});

export const searchCustomListingsError = e => ({
  type: SEARCH_CUSTOM_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCustomListings = (config, sections) => async (dispatch, getState, sdk) => {
  try {
    dispatch(searchCustomListingsRequest());
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);

    const customDataSections = sections.filter(
      section =>
        section?.sectionId &&
        section.sectionId.includes('fetch-custom-data') &&
        Array.isArray(section?.blocks) &&
        section.blocks.length > 0
    );

    // Params to copy from query string if present
    const allowedParams = ['pub_categoryLevel1', 'sort', 'keywords', 'bounds'];

    const promises = customDataSections.map(section => {
      const href = section.callToAction.href.startsWith('/s?')
        ? section.callToAction.href.slice(2)
        : section.callToAction.href;

      const queryParams = parse(href, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      // Build dynamic params by filtering allowed params that exist in queryParams
      const dynamicParams = allowedParams.reduce((acc, param) => {
        if (queryParams[param]) {
          acc[param] = queryParams[param];
        }
        return acc;
      }, {});

      return sdk.listings.query({
        include: ['author', 'author.profileImage', 'images', 'currentStock'],
        'fields.image': imageVariantInfo.fieldsImage,
        ...imageVariantInfo.imageVariants,
        'limit.images': 1,
        perPage: 12,
        pub_listingType: 'sell-new-products',
        ...dynamicParams,
      });
    });

    const responses = await Promise.all(promises);
    const sanitizeConfig = { listingFields: config?.listing?.listingFields };

    responses.forEach((response, index) => {
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(
        searchCustomListingsSuccess({ ...response, id: customDataSections[index].sectionId })
      );
    });

    return responses;
  } catch (error) {
    return dispatch(searchCustomListingsError(storableError(error)));
  }
};

export const loadData = (params, search) => dispatch => {
  const pageId = params.pageId;
  const pageAsset = { [pageId]: `content/pages/${pageId}.json` };
  const hasFallbackContent = false;
  return dispatch(fetchPageAssets(pageAsset, hasFallbackContent));
};

// ================ Selectors ================ //

export const cmsInProgressSelector = state => state.CMSPage.inProgress;
export const cmsErrorSelector = state => state.CMSPage.error;
export const cmsCustomListingsSelector = (state, sectionId) =>
  getListingsById(state, state.CMSPage.customListingIds[sectionId] || []) || [];
